/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './GridPersonalizationMenu';
import { toOptions } from 'componentregistry/utils/ManagerUtils';

const GridPersonalizationMenuManager: ComponentManagerFn<
  'GridPersonalizationMenu'
> = (platform: Platform) => {
  return {
    ...baseComponentManager<'GridPersonalizationMenu'>(
      platform,
      'GridPersonalizationMenu',
    ),
    type: 'GridPersonalizationMenu',
    name: 'Grid Personalization Menu',
    isChildAllowed: (page, item, childType) =>
      (['Popover', 'PulseTip'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      gridId: {
        label: 'Grid Id',
        type: 'ItemID',
        helpText:
          'Specify the Item ID of the associated Grid Component whose column settings (column order, column width & column visible state) needs to be stored.',
        category: 'General',
        initialFilter: 'DataGrid',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'DataGrid' && item?.type !== 'CanvasGrid') {
            return `Item id '${value}' doesn't belong to a Grid`;
          }
          return '';
        },
      },
      pivotId: {
        label: 'Pivot Table Item Id',
        type: 'ItemID',
        category: 'General',
        initialFilter: 'PivotTable',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (item?.type !== 'PivotTable') {
            return `Item id '${value}' doesn't belong to a Pivot Table`;
          }
          return '';
        },
      },
      idType: {
        category: 'General',
        label: 'Bind To',
        type: 'select',
        options: { DataGrid: 'Data Grid', PivotTable: 'Pivot Table' },
      },
      queryOwnerPublicView: {
        label: 'Disable Public View For Owner',
        type: 'boolean',
        category: 'Search',
        helpText:
          'Enable this to show "Make this view public" option for owner the view',
      },
      showPublicView: {
        label: 'Enable Public View',
        type: 'booleanExpression',
        category: 'Search',
        helpText:
          'Enable this to show "Make this view public" option in the saved search popup. Note: Owner of the view and Administrator will always be able to see "Make this view public" option',
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
      },
      margin: {
        label: 'Margin',
        type: 'margin',
        category: 'Spacing',
      },
      padding: {
        label: 'Padding',
        type: 'padding',
        category: 'Spacing',
      },
      labelFontSize: {
        category: 'Look & Feel',
        label: 'Label Font Size',
        type: 'numberInput',
      },
      labelFontWeight: {
        label: 'Label Font Weight',
        type: 'number',
        category: 'Look & Feel',
        min: 100,
        max: 900,
        step: 100,
      },
      labelFontFamily: {
        label: 'Label Font Family',
        type: 'string',
        category: 'Look & Feel',
      },
      labelFontStyle: {
        label: 'Label Font Style',
        type: 'select',
        options: toOptions(['italic', 'normal', 'oblique']),
        category: 'Look & Feel',
      },
    },
    isPropertyVisible: (property, itemData) => {
      switch (property) {
        case 'pivotId':
          return itemData.idType === 'PivotTable';
        case 'gridId':
          return itemData.idType === 'DataGrid';
        default:
          return true;
      }
    },
    getDisplayText: (item) => item.props.placeholder,
    category: 'Forms',
    icon: { iconName: 'list-dropdown' },
    description: `**Overview**<br/>
The Grid Personalization Menu is a persistent configuration tool for Data Grids and Pivot Tables. It allows end-users to customize their view of a data set (e.g., reordering columns, resizing widths, hiding fields) and save those preferences as a named "View" (e.g., "My Weekly Report").
Instead of forcing every user to see the same rigid table layout, this component empowers them to create their own personalized workspaces.

**1. Binding (Connecting to a Grid)**<br/>
To function, this menu must be linked to a specific data component on the page.
* **Bind To:** Select the type of component you want to control (Options: Data Grid or Pivot Table).
* **Grid Id / Pivot Table Item Id:** Select the specific Item ID of the target component.
* **Validation:** The system ensures you only select valid ID types (e.g., you cannot bind this to a Button).

**2. View Management (Public vs. Private)**<br/>
Users can save multiple views. By default, views are private (visible only to the creator). However, you can control the ability to share views globally.
* **Enable Public View (Show Public View):**
    * **Expression:** A boolean expression (e.g., \`$.user.role === 'Manager'\`).
    * **Behavior:** If true, the user sees a "Make this view public" checkbox when saving a new view. Public views are visible to all users of the application.
* **Disable Public View For Owner:** Explicitly prevents the original creator (Owner) from making their own private view public later on.

**3. Appearance**<br/>
* **Placeholder:** Text displayed on the menu button (e.g., "Select View" or "My Views").
* **Label Styling:** Full control over the font appearance (Font Size, Font Weight, Font Family, Font Style) to match your application's toolbar design.

**4. Composition**<br/>
* **Allowed Children:**
    * **Popover:** Display a popup helper or extra info when interacting with the menu.
    * **PulseTip:** Add a pulsing notification to draw attention to the menu (e.g., "New feature: Save your view!").`,
  };
};

export default GridPersonalizationMenuManager;
